import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { ColDef } from 'ag-grid-enterprise';
import * as dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { DateTime } from 'luxon';
import { DropDownOption } from 'src/app/product-capture/model/interfaces/drop-down-option.interface';
import { LAST_DAY_OF_PERIOD } from 'src/app/static-data/models/consts/recurring-fee.const';
import { DateUtils } from 'src/app/utils/date.utils';
import { FEES_MANAGEMENT_LABELS } from '../labels.const';
import { RollDayConfig, SupplementAgreement } from '../supplement-agreement/supplement-agreement.consts';

dayjs.extend(isSameOrBefore);

export function enumToDropdownOption(enumType: any): DropDownOption[] {
  return Object.keys(enumType).map((key: string) => ({
    id: key,
    displayValue: enumType[key],
  }));
}

export type FeesLabelsKey = typeof FEES_MANAGEMENT_LABELS | undefined;

export type ColumnOfFees<T extends keyof FeesLabelsKey> = 
  keyof FeesLabelsKey[T] | (string & {});

export function labelsToColumns<T extends keyof FeesLabelsKey>(key: T, ignoreColumns?: ColumnOfFees<T>[]): ColDef[] {
  const labels = FEES_MANAGEMENT_LABELS[key];

  let filteredLabels = Object.keys(labels) as ColumnOfFees<T>[];

  if (ignoreColumns) {
    filteredLabels = filteredLabels.filter((label) => !ignoreColumns.includes(label));
  }

  return filteredLabels.map((key: string, index: number) => ({
    field: key as string,
    headerName: String(labels[key]),
    sortable: true,
    initialHide: index > 3,
    flex: 1,
    minWidth: 200,
    suppressAutoSize: true,
    type: columnType(key),
    filter: true,
  }));
}

export function columnType<T extends keyof FeesLabelsKey>(key: ColumnOfFees<T>): string {
  if (key === 'date' || key === 'actualDate') {
    return 'dateFormat';
  }
  if (key === 'balance') {
    return 'numberFormat';
  }
  if (key === 'price' || key === 'position' || key === 'rate') {
    return 'compoundValueFormat';
  }

  return null;
}

export function calculateRollDayConfig(supplementAgreement: SupplementAgreement): RollDayConfig {

  let { 
    evaluationStartDate, 
    evaluationEndDate,
    evaluationFrequency: frequencyMonths,
    startOfFirstPeriod: startMonth,
    evaluationDay,
  } = supplementAgreement;

  if (!evaluationStartDate || !evaluationEndDate || !frequencyMonths || !startMonth || !evaluationDay) {
    return null;
  }
  
  startMonth = Number(startMonth);

  function isLastDayOfMonth(year: number, month: number, day: number | string): boolean {
    const date = DateTime.local(year, month, Number(day));
    const lastDayOfMonth = date.endOf('month');
    return date.hasSame(lastDayOfMonth, 'day');
  }

  function getLastDayOfMonth(year: string | number, month: string | number): number {
      const date = DateTime.local(Number(year), Number(month), 1);
      return date.endOf('month').day;
  }

  const startOfFirstPeriodDate = DateUtils.convertNgbDateToString(evaluationStartDate as NgbDateStruct);
  const endOfLastPeriodDate = DateUtils.convertNgbDateToString(evaluationEndDate as NgbDateStruct);
  let lastDayInPeriod: number | string = evaluationDay;

  if (lastDayInPeriod === LAST_DAY_OF_PERIOD) {
    lastDayInPeriod = getLastDayOfMonth(startOfFirstPeriodDate.split('-')[0], startMonth);
  }
  
  // Parse dates into DateTime objects for comparison later on
  const startOfFirstPeriod = DateTime.fromISO(startOfFirstPeriodDate)
  const endOfLastPeriod = DateTime.fromISO(endOfLastPeriodDate)

  // Extract the number of months from the variable
  const numMonths = parseInt(frequencyMonths.slice(0, -1), 10);

  // We start in the same year as the start date
  let startYear = startOfFirstPeriod.year;

  // Find an appropriate month start based on whether lastDayInPeriod is the last day of the month or not
  let month = ((startMonth + numMonths) % 12)
  if (isLastDayOfMonth(startYear, startMonth, lastDayInPeriod)) {
      startMonth = month !== 1 ? ((month !== 0 ? month : 12) - 1) : 12
      lastDayInPeriod = getLastDayOfMonth(startYear, startMonth);
  } else {
      startMonth = month !== 0 ? month : 12
  }

  // Determine a base date from which to start counting
  let baseDate = DateTime.local(startYear, Number(startMonth), Number(lastDayInPeriod));

  // Initialise some state variables
  let currentEndOfPeriod = baseDate;
  let mult = 1;

  // Step 1: Find the end of the first period
  while (currentEndOfPeriod <= startOfFirstPeriod) {
      currentEndOfPeriod = baseDate.plus({ months: numMonths * mult });
      mult += 1;
  }

  const endOfFirstPeriod = currentEndOfPeriod;

  // Step 2: Find the start of the last period
  let startOfLastPeriod: DateTime = undefined;
  if (endOfFirstPeriod >= endOfLastPeriod) {
    startOfLastPeriod = startOfFirstPeriod;
  } else {
    let previousEndOfPeriod = currentEndOfPeriod;
    while (currentEndOfPeriod < endOfLastPeriod) {
        previousEndOfPeriod = currentEndOfPeriod;
        currentEndOfPeriod = baseDate.plus({ months: numMonths * mult });
        mult += 1;
    }
    startOfLastPeriod = previousEndOfPeriod.plus({ days: 1 });
  }

  // Return a RollDayConfig like object
  return {
      rollDay: null,
      irregularPeriod: null,
      endFirstPeriod: endOfFirstPeriod.toISODate(),
      startLastPeriod: startOfLastPeriod?.toISODate() ?? null
  } as RollDayConfig;
}